/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Menu, MenuItem } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

/**
 * Generic query-driven multi-select menu with checkboxes.
 * - Reads/writes a comma-separated query param in the URL.
 * - Supports mapping tokens case-insensitively to provided options.
 *
 * Props:
 * - title: string (button label)
 * - queryParam: string (URL search param key)
 * - options: Array<{ label: string, value: string }>
 * - allLabel?: string (label for the "All" option; defaults to 'All')
 * - buttonProps?: object (extra props for Button)
 */
export default function QueryMultiSelectMenu({
  title = 'Select',
  queryParam,
  options,
  allLabel = 'All',
  buttonProps,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);
  const [selected, setSelected] = useState([]); // stores canonical option.value strings

  const valueMap = useMemo(() => {
    const map = new Map();
    (options || []).forEach((opt) => {
      map.set(String(opt.value).toLowerCase(), opt.value);
      map.set(String(opt.label).toLowerCase(), opt.value);
    });
    return map;
  }, [options]);

  // Sync state from URL
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const raw = sp.get(queryParam);
    if (!raw) {
      setSelected([]);
      return;
    }
    const tokens = raw.split(',').map((s) => decodeURIComponent(s).trim()).filter(Boolean);
    const mapped = tokens
      .map((tok) => valueMap.get(tok.toLowerCase()) || null)
      .filter(Boolean);
    // dedupe
    const unique = Array.from(new Set(mapped));
    setSelected(unique);
  }, [location.search, queryParam, valueMap]);

  const updateURL = (values) => {
    const sp = new URLSearchParams(location.search);
    if (!values || values.length === 0) {
      sp.delete(queryParam);
    } else {
      sp.set(queryParam, values.join(','));
    }
    navigate({ search: sp.toString() });
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    if (value === '__ALL__') {
      setSelected([]);
      updateURL([]);
      return;
    }
    const canonical = valueMap.get(String(value).toLowerCase());
    if (!canonical) return;
    if (checked) {
      const next = Array.from(new Set([...selected, canonical]));
      setSelected(next);
      updateURL(next);
    } else {
      const next = selected.filter((v) => v !== canonical);
      setSelected(next);
      updateURL(next);
    }
  };

  const handleClick = (event) => setAnchor(event.currentTarget);
  const handleClose = () => setAnchor(null);

  return (
    <Box>
      <Button
        id={`${queryParam}-menu-button`}
        aria-controls={open ? `${queryParam}-menu` : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        {...buttonProps}
      >
        {title}
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Button>

      <Menu
        id={`${queryParam}-menu`}
        anchorEl={anchor}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': `${queryParam}-menu-button` }}
        PaperProps={{
          style: { width: '85%', maxWidth: '100%', maxHeight: '300px', left: 0, right: 0 },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', width: '100%', maxHeight: '300px', overflow: 'auto', overflowY: 'hidden' }}>
          <MenuItem>
            <FormControlLabel
              label={`${allLabel}`}
              control={<Checkbox checked={selected.length === 0} value="__ALL__" onChange={handleCheckboxChange} />}
            />
          </MenuItem>

          {(options || []).map((opt) => (
            <MenuItem key={opt.value}>
              <FormControlLabel
                label={opt.label}
                control={<Checkbox checked={selected.includes(opt.value)} value={opt.value} onChange={handleCheckboxChange} />}
              />
            </MenuItem>
          ))}
        </Box>
      </Menu>
    </Box>
  );
}
QueryMultiSelectMenu.propTypes = {
  title: PropTypes.string.isRequired,
  queryParam: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  allLabel: PropTypes.string,
  buttonProps: PropTypes.object,
};
